/**
 * V2FontsSection - Editor section for V2 theme fonts
 *
 * Uses TypographyStyle instead of V1 LabelBlock. Key differences:
 * - `align` field ('start'|'center'|'end') instead of `textAlign`
 * - Additional fields: lineHeight
 *
 * Provides font family selector and font style settings for:
 * - main: Main font family for all text
 * - title: Title text styling (TypographyStyle)
 * - subtitle: Subtitle text styling (TypographyStyle)
 */

import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Autocomplete,
} from '@mui/material';
import { SAccordion } from '@modules/ui-components';
import { ColorInput } from '@modules/reactFlow/components/shared/styleFields/ColorInput';
import {
  FONT_FAMILY_OPTIONS,
  FONT_WEIGHT_OPTIONS_SHORT,
  NUMBER_0_50_OPTIONS,
} from '@modules/reactFlow/config/atomicFields/options';
import type { ThemeV2Fonts, TypographyStyle } from '../../types/themesV2.types';
import { useFontLabels } from '@modules/font-config/context/FontLabelsContext';

// V2 text alignment options (RTL-aware)
const ALIGN_OPTIONS = [
  { value: 'start', label: 'Start' },
  { value: 'center', label: 'Center' },
  { value: 'end', label: 'End' },
];

export interface V2FontsSectionProps {
  /** Current font values (V2 TypographyStyle) */
  fonts: ThemeV2Fonts;
  /** Callback when font setting changes */
  onChange: (path: string, value: unknown) => void;
  /** Whether section is disabled */
  disabled?: boolean;
  /** Whether section is expanded by default */
  defaultExpanded?: boolean;
}

/**
 * V2FontsSection - SAccordion-based font editor for V2 themes
 */
export const V2FontsSection: React.FC<V2FontsSectionProps> = ({
  fonts,
  onChange,
  disabled = false,
  defaultExpanded = false,
}) => {
  const fontLabels = useFontLabels();

  const handleSelectChange = useCallback(
    (path: string) => (event: { target: { value: unknown } }) => {
      onChange(path, event.target.value);
    },
    [onChange]
  );

  const handleNumberComboChange = useCallback(
    (path: string) => (value: string | number | null) => {
      if (value != null) {
        const num = typeof value === 'number' ? value : parseFloat(value);
        if (!isNaN(num)) {
          onChange(path, num);
        }
      }
    },
    [onChange]
  );

  const handleColorChange = useCallback(
    (path: string) => (value: unknown) => {
      onChange(path, value);
    },
    [onChange]
  );

  // Render TypographyStyle fields (V2 — uses align instead of textAlign)
  const renderTypographyFields = (prefix: string, style: TypographyStyle, label: string) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {/* Font Family (individual override) */}
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel id={`${prefix}-family-label`}>Font Family</InputLabel>
          <Select
            labelId={`${prefix}-family-label`}
            value={style.fontFamily ?? fonts.main.fontFamily}
            onChange={handleSelectChange(`${prefix}.fontFamily`)}
            label="Font Family"
            disabled={disabled}
            aria-label={`${label} Font Family`}
          >
            {FONT_FAMILY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {fontLabels[opt.value] || opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Font Weight - Autocomplete with freeSolo */}
        <Box sx={{ width: 80 }}>
          <Typography variant="caption" sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
            Font weight
          </Typography>
          <Autocomplete
            freeSolo
            disableClearable
            size="small"
            disabled={disabled}
            options={FONT_WEIGHT_OPTIONS_SHORT.map((o) => o.value)}
            value={String(style.fontWeight ?? 400)}
            onChange={(_, newValue) => handleNumberComboChange(`${prefix}.fontWeight`)(newValue)}
            onInputChange={(_, newValue, reason) => {
              if (reason === 'input' && newValue) {
                handleNumberComboChange(`${prefix}.fontWeight`)(newValue);
              }
            }}
            renderInput={(params) => <TextField {...params} variant="standard" />}
            sx={{ width: '100%' }}
          />
        </Box>

        {/* Font Size - Autocomplete with freeSolo */}
        <Box sx={{ width: 70 }}>
          <Typography variant="caption" sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
            Font size
          </Typography>
          <Autocomplete
            freeSolo
            disableClearable
            size="small"
            disabled={disabled}
            options={NUMBER_0_50_OPTIONS.map((o) => o.value)}
            value={String(style.fontSize ?? 16)}
            onChange={(_, newValue) => handleNumberComboChange(`${prefix}.fontSize`)(newValue)}
            onInputChange={(_, newValue, reason) => {
              if (reason === 'input' && newValue) {
                handleNumberComboChange(`${prefix}.fontSize`)(newValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                inputProps={{ ...params.inputProps, 'data-testid': `theme-v2-font-${prefix.replace('.', '-')}-font-size` }}
              />
            )}
            sx={{ width: '100%' }}
          />
        </Box>

        {/* Color */}
        <Box sx={{ width: 120 }}>
          <Typography variant="caption" sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
            Color
          </Typography>
          <ColorInput
            value={style.color ?? '#ffffff'}
            onChange={handleColorChange(`${prefix}.color`)}
            disabled={disabled}
          />
        </Box>

        {/* Align (V2-specific: start/center/end) */}
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <InputLabel id={`${prefix}-align-label`}>Align</InputLabel>
          <Select
            labelId={`${prefix}-align-label`}
            value={style.align ?? 'center'}
            onChange={handleSelectChange(`${prefix}.align`)}
            label="Align"
            disabled={disabled}
            aria-label={`${label} Align`}
          >
            {ALIGN_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );

  return (
    <SAccordion
      title="Fonts"
      collapsible
      defaultExpanded={defaultExpanded}
      disabled={disabled}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Main Font Family */}
        <FormControl fullWidth size="small">
          <InputLabel id="v2-main-font-label">Main Font Family</InputLabel>
          <Select
            labelId="v2-main-font-label"
            value={fonts.main.fontFamily}
            onChange={handleSelectChange('fonts.main.fontFamily')}
            label="Main Font Family"
            disabled={disabled}
            aria-label="Main Font Family"
          >
            {FONT_FAMILY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {fontLabels[opt.value] || opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Title Font Settings (TypographyStyle) */}
        {renderTypographyFields('fonts.title', fonts.title, 'Title')}

        {/* Subtitle Font Settings (TypographyStyle) */}
        {renderTypographyFields('fonts.subtitle', fonts.subtitle, 'Subtitle')}
      </Box>
    </SAccordion>
  );
};

export default V2FontsSection;
